import { Link } from "react-router-dom";
import { Check, MapPin, Phone, ArrowUpRight } from "lucide-react";
import { SERVICES, AREAS, COMPANY } from "@/data/site";
import PageHeader from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import CTASection from "@/components/CTASection";
import { trackCallConversion } from "@/lib/analytics";
import { useSeo } from "@/hooks/useSeo";

export default function LancashireArea() {
  useSeo(
    "Exterior Cleaning Lancashire | Roof, Render & Driveway Cleaning | StoneCrest",
    "Professional exterior cleaning across Lancashire for residential and commercial properties, including roof cleaning, render softwashing, driveway cleaning and gutter clearing.",
    "https://stonecrestexteriors.co.uk/areas/lancashire"
  );

  return (
    <div data-testid="lancashire-page">
      <PageHeader
        overline="Areas We Cover · Lancashire"
        lines={["Exterior cleaning", "across Lancashire."]}
        subtitle="StoneCrest Exterior Specialists provides premium, fully-insured exterior cleaning for residential and commercial properties throughout Lancashire — from homes and managed lettings to offices, shops and retail premises. Safe softwashing and careful pressure cleaning that transform kerb appeal without damaging your property."
      />

      {/* Intro content */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto pb-4">
        <div className="max-w-3xl space-y-5 text-lg text-[var(--muted)] leading-relaxed">
          <Reveal>
            <p>
              From period stone cottages and red-brick terraces to modern
              rendered homes, properties across Lancashire face the same
              challenge: a mild, damp climate that encourages moss, algae and
              organic staining on roofs, render, driveways and gutters.
              StoneCrest removes that build-up using gentle, professional
              methods and premium equipment, restoring a clean, cared-for
              finish.
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <p>
              Every job begins with a free, no-obligation property assessment
              and a clear, fixed-price quote — so you always know the cost
              before we start. Wherever you are in Lancashire, you'll deal with
              a professionally trained, fully insured team that respects your
              home and communicates clearly from first enquiry to final
              inspection.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Services across Lancashire */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-20">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
            Our Services in Lancashire
          </p>
        </Reveal>

        <Reveal delay={0.05}>
          <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-10 max-w-2xl">
            Specialist exterior cleaning, wherever you are in the county.
          </h2>
        </Reveal>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
          {SERVICES.map((s) => (
            <Link
              key={s.slug}
              to={`/services/${s.slug}`}
              data-testid={`lancs-service-${s.slug}`}
              className="group flex items-center gap-3 border-t border-[var(--border)] py-4 hover:text-[var(--oxblood)] transition-colors"
            >
              <Check className="w-5 h-5 text-[var(--oxblood)] shrink-0" />

              <span className="group-hover:translate-x-1 transition-transform">
                {s.name}
              </span>
            </Link>
          ))}
        </div>
      </section>

      {/* Towns we cover */}
      <section className="bg-[var(--bone)]">
        <div className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-20">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-[var(--oxblood)] mb-4">
              Towns We Serve
            </p>
          </Reveal>

          <Reveal delay={0.05}>
            <h2 className="font-serif font-light text-3xl md:text-5xl leading-tight mb-10 max-w-2xl">
              Proudly cleaning homes right across Lancashire.
            </h2>
          </Reveal>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-[var(--border)] border border-[var(--border)]">
            {AREAS.map((a) => (
              <Link
                key={a}
                to={`/areas/${a.toLowerCase()}`}
                data-testid={`lancs-town-${a.toLowerCase()}`}
                className="group bg-white px-7 py-6 hover:bg-[var(--oxblood)] hover:text-white transition-colors duration-400"
              >
                <span className="font-serif text-2xl flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <MapPin
                      className="w-5 h-5 text-[var(--oxblood)] group-hover:text-white transition-colors"
                      strokeWidth={1.4}
                    />
                    {a}
                  </span>

                  <ArrowUpRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                </span>
              </Link>
            ))}
          </div>

          <p className="mt-6 text-sm text-[var(--muted)]">
            Don't see your town? We cover many more Lancashire towns and
            villages —{" "}
            <Link
              to="/contact"
              className="text-[var(--oxblood)] underline"
            >
              get in touch
            </Link>{" "}
            to check your area.
          </p>
        </div>
      </section>

      {/* CTA row */}
      <section className="px-6 md:px-12 max-w-7xl mx-auto py-16 md:py-20">
        <div className="max-w-3xl">
          <h2 className="font-serif font-light text-3xl md:text-4xl mb-6">
            Book your Lancashire exterior clean.
          </h2>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/quote"
              className="bg-[var(--oxblood)] text-white px-8 py-4 rounded-full tracking-wide hover:bg-[var(--oxblood-hover)] transition-colors text-center"
            >
              Get a Free Quote
            </Link>

            <a
              href={COMPANY.phoneHref}
              onClick={trackCallConversion}
              className="border border-[var(--ink)] px-8 py-4 rounded-full tracking-wide hover:bg-[var(--ink)] hover:text-white transition-all flex items-center justify-center gap-2"
            >
              <Phone className="w-4 h-4" />
              {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>

      <CTASection title="Premium exterior care, right across Lancashire." />
    </div>
  );
}